import React, { useState, useRef } from 'react';
import mainTheme from '../ProjectSettings.module.scss';
import theme from './ProjectSettingsTags.module.scss';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useAsyncState } from '@/utils/hooks';
import { editProjectTags, getProjectTagsExtended } from '@/services/project-tags';
import { LoadingIndicator, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Button, ButtonBar } from '@ygtechnologies/rubus-ui';
import EditTags from '@/components/Tags/EditTags';
import { Input } from '@/components/generic-controls';
import { max } from 'lodash-es';

interface ProjectSettingsTagsProps {
    projectId: number;
}

const ProjectSettingsTags: React.FC<ProjectSettingsTagsProps> = ({ projectId }) => {
    const [inputText, setInputText] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const [tags, setTags, reloadTags] = useAsyncState(() => getProjectTagsExtended(projectId), [], null);
    const newItemCounter = useRef(0);

    const handleTagAdd = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!tags) {
            return;
        }
        if (!inputText) {
            return;
        }

        if (tags!.some((t) => t.label.toLowerCase() === inputText.trim().toLowerCase())) {
            Toast.error(<Trans>A tag with this name already exists</Trans>);
            return;
        }

        setTags([
            ...tags!,
            {
                label: inputText,
                residentialUnitTagCount: 0,
                linkGroupTagCount: 0,
                changeRequestTagCount: 0,
                orderTagCount: 0,
                externalAgreementCount: 0,
                caseTagCount: 0,
                documentTagCount: 0,
                totalCount: 0,
                aftermarketCount: 0,
                id: --newItemCounter.current,
                canBeDeleted: true,
                canBeRenamed: true,
                sortOrder: max(tags!.map((t) => t.sortOrder))! + 1,
            },
        ]);
        setInputText('');
    };

    const handleSave = async () => {
        if (tags === null) {
            return;
        }
        setIsSaving(true);

        try {
            await editProjectTags(
                projectId,
                tags.map((t, i) => ({ id: t.id < 0 ? undefined : t.id, label: t.label, type: t.type, sortOrder: i }))
            );
            await reloadTags();
            Toast.success(<Trans>Saved tags</Trans>);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <AlertBanner type="warning">
                <Trans context="Banner on project settings page" id="project_settings_tags_banner_info">
                    This is tags.
                </Trans>
            </AlertBanner>
            <div className={mainTheme.section}>
                {!tags ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <h5>
                            <Trans>Create a new tag</Trans>
                        </h5>
                        <form onSubmit={handleTagAdd} className="flex">
                            <Input
                                placeholder={t`Create new tag.`}
                                className="form-control"
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                            />
                            <Button className={theme.addButton} type="submit" variant="tertiary">
                                <Trans>Add tag</Trans>
                            </Button>
                        </form>
                        <h5 className={theme.header}>
                            <Trans>Active tags</Trans>
                        </h5>
                        <EditTags onChange={setTags} tags={tags} />
                    </>
                )}
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button onClick={handleSave} variant="primary" loading={isSaving}>
                            <Trans>Save</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </div>
        </>
    );
};

export default ProjectSettingsTags;
